import type { JSX } from "react";
import { Formik, Form, FastField, Field, FieldProps } from "formik";
import * as yup from "yup";
import { AppTextField } from "./text-field";
import { validationMessages } from "../utils/validation/messages";
import { CustomerType } from "../models/customer-type";
import {
  MenuItem,
  Box,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Autocomplete,
  TextField,
} from "@mui/material";
import { FormPrompt } from "./form-prompt";
import { Customer } from "../models/customer";
import { useMemo, useState } from "react";
import { IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

export interface CustomerFormValues {
  name: string;
  typeId: string;
  contactFirstName: string;
  contactLastName: string;
  contactEmail: string;
  contactPhoneNumber: string;
  businessEmail: string;
  businessPhoneNumber: string;
  otherPhoneNumber: string;
  addressLine1: string;
  addressLine2: string;
  locality: string;
  postalCode: string;
  region: string;
  country: string;
  website: string;
  generalComments: string;
  deliveryInstructions: string;
  paymentInstructions: string;
  deliveryRoute: string;
  archived: boolean;
}

const validationSchema = yup.object({
  name: yup.string().trim().required(validationMessages.Required),
  typeId: yup.string().required(validationMessages.Required),
  contactFirstName: yup.string(),
  contactLastName: yup.string(),
  contactEmail: yup.string().email(validationMessages.Email),
  contactPhoneNumber: yup.string(),
  businessEmail: yup.string().email(validationMessages.Email),
  businessPhoneNumber: yup.string(),
  otherPhoneNumber: yup.string(),
  addressLine1: yup.string(),
  addressLine2: yup.string(),
  locality: yup.string(),
  postalCode: yup.string(),
  region: yup.string(),
  country: yup.string(),
  website: yup.string(),
  generalComments: yup.string(),
  deliveryInstructions: yup.string(),
  paymentInstructions: yup.string(),
  deliveryRoute: yup.string(),
  archived: yup.boolean(),
});

export default function CustomerForm(props: {
  children: JSX.Element;
  initialValues?: CustomerFormValues;
  onSubmit: (values: CustomerFormValues) => void;
  customerTypes: CustomerType[];
  customers: Customer[];
  saved?: boolean;
}) {
  const [archivedRoutes, setArchivedRoutes] = useState<string[]>([]);

  const deliveryRoutes = useMemo(
    () =>
      Array.from(
        new Set(
          props.customers
            .map((c) => (c.deliveryRoute ?? "").trim())
            .filter(
              (route) =>
                route &&
                !archivedRoutes.some(
                  (a) => a.toLowerCase() === route.toLowerCase(),
                ),
            ),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [props.customers, archivedRoutes],
  );

  const initialValues: CustomerFormValues = useMemo(() => {
    const defaults: CustomerFormValues = {
      name: "",
      typeId: "",
      contactFirstName: "",
      contactLastName: "",
      contactEmail: "",
      contactPhoneNumber: "",
      businessEmail: "",
      businessPhoneNumber: "",
      otherPhoneNumber: "",
      addressLine1: "",
      addressLine2: "",
      locality: "",
      postalCode: "",
      region: "",
      country: "",
      website: "",
      generalComments: "",
      deliveryInstructions: "",
      paymentInstructions: "",
      deliveryRoute: "",
      archived: false,
    };

    if (!props.initialValues) return defaults;

    return {
      ...defaults,
      ...props.initialValues,
      typeId: props.initialValues.typeId?.toString() ?? "",
    };
  }, [props.initialValues]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values, formikHelpers) => {
        await props.onSubmit(values);
        formikHelpers.resetForm({ values });
      }}
    >
      {(f) => (
        <>
          <FormPrompt form={f} saved={props.saved ?? false} />

          <Form noValidate>
            <Box sx={{ pb: 10 }}>
              <Box mb={2}>
                <FormControl
                  variant="standard"
                  error={f.touched.archived && Boolean(f.errors.archived)}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="archived"
                        color="primary"
                        onChange={f.handleChange}
                        checked={f.values.archived}
                      />
                    }
                    label="Archived"
                  />
                  {f.touched.archived && f.errors.archived && (
                    <FormHelperText>{f.errors.archived}</FormHelperText>
                  )}
                </FormControl>
              </Box>

              <Box mb={2}>
                <FastField name="name">
                  {(fieldProps: FieldProps) => (
                    <AppTextField {...fieldProps} autoFocus label="Name" />
                  )}
                </FastField>
              </Box>

              <Box mb={2}>
                <Field name="typeId">
                  {(fieldProps: FieldProps) => (
                    <AppTextField
                      {...fieldProps}
                      select
                      label="Type"
                      onChange={(e) => {
                        const value = e.target.value;
                        f.setFieldValue("typeId", value.toString());
                      }}
                    >
                      {props.customerTypes
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((customerType) => (
                          <MenuItem
                            key={customerType.id}
                            value={customerType.id.toString()}
                          >
                            {customerType.name}
                          </MenuItem>
                        ))}
                    </AppTextField>
                  )}
                </Field>
              </Box>

              <Box mb={2}>
                <Autocomplete<string, false, false, true>
                  options={deliveryRoutes}
                  freeSolo
                  value={f.values.deliveryRoute || ""}
                  inputValue={f.values.deliveryRoute || ""}
                  onInputChange={(_e, value) => {
                    f.setFieldValue("deliveryRoute", value);
                  }}
                  onChange={(_e, value) => {
                    f.setFieldValue("deliveryRoute", value ?? "");
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                      >
                        <span>{option}</span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setArchivedRoutes((prev) => [...prev, option]);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Delivery Route"
                      name="deliveryRoute"
                      variant="outlined"
                    />
                  )}
                />
              </Box>

              {[
                { name: "contactFirstName", label: "Contact First Name" },
                { name: "contactLastName", label: "Contact Last Name" },
                { name: "contactEmail", label: "Contact Email", type: "email" },
                { name: "contactPhoneNumber", label: "Contact Phone Number" },
                {
                  name: "businessEmail",
                  label: "Business Email",
                  type: "email",
                },
                { name: "businessPhoneNumber", label: "Business Phone Number" },
                { name: "otherPhoneNumber", label: "Other Phone Number" },
                {
                  name: "addressLine1",
                  label: "Address Line 1 - Auto fills to Deliveries Sheet",
                },
                { name: "addressLine2", label: "Address Line 2" },
                { name: "locality", label: "City" },
                { name: "postalCode", label: "Postal Code" },
                { name: "region", label: "Province / State" },
                { name: "country", label: "Country" },
                { name: "website", label: "Website" },
                { name: "generalComments", label: "General Comments" },
                {
                  name: "deliveryInstructions",
                  label: "Delivery Instructions",
                },
                { name: "paymentInstructions", label: "Payment Instructions" },
              ].map((field) => (
                <Box mb={2} key={field.name}>
                  <FastField
                    component={AppTextField}
                    name={field.name}
                    label={field.label}
                    type={field.type}
                  />
                </Box>
              ))}
            </Box>

            {props.children}
          </Form>
        </>
      )}
    </Formik>
  );
}
